/**
 * Tahap 6A — Inventory & HPP Foundation UI
 * Bahasa Indonesia · Modern & konsisten dengan tema existing (slate palette).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Client-side mirror of _PERMS in backend/inventory.py — dipakai untuk sembunyikan tombol aksi
// bagi peran yang tidak berwenang. Backend tetap enforce 403.
const INV_PERMS_BY_ROLE = {
  super_admin: ["*"],
  gudang: ["INV_LIHAT", "INV_TRANSAKSI", "INV_TRANSFER", "INV_ADJUSTMENT", "INV_OPNAME", "INV_BATCH", "INV_KARTU_STOK"],
  akuntansi: ["INV_LIHAT", "INV_KARTU_STOK", "INV_HPP_LIHAT", "INV_INTEGRITY", "INV_OPENING_BALANCE"],
  keuangan: ["INV_LIHAT", "INV_KARTU_STOK", "INV_HPP_LIHAT"],
  auditor: ["INV_LIHAT", "INV_KARTU_STOK", "INV_INTEGRITY", "INV_HPP_LIHAT"],
  direktur_utama: ["INV_LIHAT", "INV_KARTU_STOK", "INV_HPP_LIHAT", "INV_INTEGRITY", "INV_OPNAME_SETUJUI"],
  direktur_operasional: ["INV_LIHAT", "INV_KARTU_STOK", "INV_OPNAME_SETUJUI"],
  perpajakan: ["INV_LIHAT"],
  viewer: ["INV_LIHAT"],
};

function useInvCan() {
  const { user } = useAuth();
  const role = user?.role;
  const perms = INV_PERMS_BY_ROLE[role] || [];
  const isSuper = role === "super_admin" || perms.includes("*");
  return (p) => isSuper || perms.includes(p);
}

/* Banner reusable untuk halaman read-only ketika user tidak punya akses.
   Menghindari tabel kosong yang membingungkan. */
export function NoAccessBanner({ perm }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="no-access-banner">
      Tidak berwenang mengakses data ini{perm ? ` — perlu izin ${perm}` : ""}. Hubungi administrator jika Anda membutuhkan akses.
    </div>
  );
}
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import { HorizontalBarCard, BarCard, COLORS as CHART_COLORS } from "@/components/charts/DashboardCharts";
import {
  Package, Warehouse, ArrowRightLeft, ClipboardList, Calendar, ShieldCheck,
  Boxes, RefreshCw, PlusCircle, Settings, TrendingDown, AlertTriangle, Cog,
} from "lucide-react";

const MOVEMENT_TYPES = [
  "SALDO_AWAL", "PEMBELIAN", "PENJUALAN", "RETUR_JUAL", "RETUR_BELI",
  "TRANSFER_KELUAR", "TRANSFER_MASUK", "ADJUSTMENT_PLUS", "ADJUSTMENT_MINUS",
  "OPNAME_PLUS", "OPNAME_MINUS", "RUSAK", "KEDALUWARSA", "SAMPLE_PROMO",
  "CONTRACT_MFG_RECEIPT",
];

function Crumb({ path }) {
  return <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">{path.map((p, i) => (<span key={i}>{p}{i < path.length - 1 && " › "}</span>))}</div>;
}

function Stat({ label, value, tone = "slate", sub, testid }) {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
  };
  return (
    <div className={`border rounded-md p-4 ${map[tone]}`} data-testid={testid}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="font-mono-num text-lg font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

/* ============ HUB ============ */
export function InventoryHub() {
  const [d, setD] = useState(null);
  const [aging, setAging] = useState(null);
  useEffect(() => { api.get("/inventory/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { api.get("/inventory/6c/aging").then(({ data }) => setAging(data)).catch(() => {}); }, []);
  // Phase 3 — Chart data (reuse backend by_category + aging buckets)
  const byCat = (d?.by_category || []).slice(0, 10).map((c) => ({ name: c.category || "-", value: c.value || 0 }));
  const agingBuckets = (() => {
    if (!aging?.rows) return [];
    const sum = { "0-30": 0, "31-60": 0, "61-90": 0, "91-180": 0, ">180": 0 };
    for (const r of aging.rows) {
      const b = r.bucket || "0-30";
      if (b in sum) sum[b] += Number(r.value || 0);
    }
    return Object.entries(sum).map(([name, value]) => ({ name, value }));
  })();
  return (
    <div className="space-y-6 page-fade" data-testid="inv-hub-page">
      <div>
        <Crumb path={["Persediaan"]} />
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dasbor Persediaan</h1>
        <p className="text-slate-500 mt-1 text-sm">Fondasi inventory & HPP: kartu stok, mutasi, transfer, opname, batch/kedaluwarsa, pengaturan HPP, pusat integritas.</p>
      </div>

      {d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total SKU Aktif" value={d.sku_count} tone="emerald" testid="inv-stat-sku" />
          <Stat label="Total Gudang" value={d.warehouse_count} tone="sky" testid="inv-stat-wh" />
          <Stat label="Total Nilai Persediaan" value={formatRupiah(d.total_value)} tone="blue" sub={`${d.total_qty} unit`} testid="inv-stat-value" />
          <Stat label="Batch Mendekati Expired (≤30hr)" value={d.near_expiry_batches} tone={d.near_expiry_batches > 0 ? "amber" : "slate"} sub={`${d.expired_batches} kedaluwarsa`} testid="inv-stat-near" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <HorizontalBarCard
          title="Nilai Persediaan per Kategori"
          testId="chart-inv-category"
          hint="Belum ada data kategori persediaan."
          currency
          data={byCat}
          bars={[{ key: "value", label: "Nilai", color: CHART_COLORS.baseline }]}
          height={Math.max(220, byCat.length * 36 + 60)}
        />
        <BarCard
          title="Aging Persediaan (nilai per bucket)"
          testId="chart-inv-aging"
          hint="Belum ada data aging persediaan."
          currency
          data={agingBuckets}
          bars={[{ key: "value", label: "Nilai", color: CHART_COLORS.info }]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["/persediaan/kartu-stok", "Kartu Stok", "Riwayat mutasi per SKU.", ClipboardList],
          ["/persediaan/saldo", "Saldo Persediaan", "Balance per SKU × gudang × batch.", Boxes],
          ["/persediaan/mutasi", "Mutasi Persediaan", "Buat mutasi manual (semua jenis).", ArrowRightLeft],
          ["/persediaan/transfer", "Transfer Antar Gudang", "Pindah stock antar lokasi.", Warehouse],
          ["/persediaan/transfer-order", "Transfer Order (FSM)", "DRAFT → DIKIRIM → DITERIMA · via BDP.", ArrowRightLeft],
          ["/persediaan/adjustment", "Stock Adjustment", "Adjustment +/- · Rusak · Kedaluwarsa · Sample.", TrendingDown],
          ["/persediaan/opname", "Stock Opname", "Workflow 5-tahap dengan SoD.", ClipboardList],
          ["/persediaan/batch", "Batch & Kedaluwarsa", "Track batch, expiry, near-expiry alert.", Calendar],
          ["/persediaan/saldo-awal", "Saldo Awal Persediaan", "Migrasi opening inventory idempotent.", Package],
          ["/persediaan/pengaturan-hpp", "Pengaturan HPP", "Metode costing & control negative stock.", Cog],
          ["/persediaan/integritas", "Pusat Integritas Persediaan", "8 cek: Ledger vs Balance vs GL.", ShieldCheck],
          ["/persediaan/integritas-6b", "Integritas Integrasi (6B)", "Sales/Purchase/Return ↔ Inventory ↔ HPP.", ShieldCheck],
          ["/persediaan/6c", "Dasbor Manajemen (6C)", "Executive view: nilai, aging, expiry, reorder, margin.", Boxes],
        ].map(([p, t, dsc, I]) => (
          <Card key={p} className="hover:border-slate-400 transition"><CardContent className="p-4">
            <div className="flex items-center gap-2"><I className="h-5 w-5 text-slate-700" /><div className="font-display font-semibold">{t}</div></div>
            <p className="text-sm text-slate-500 mt-1.5">{dsc}</p>
            <Link to={p}><Button size="sm" variant="outline" className="mt-3" data-testid={`inv-nav-${p.replace(/\//g,'-')}`}>Buka</Button></Link>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

/* ============ Saldo & Kartu Stok ============ */
export function StockBalancePage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ sku: "", warehouse_code: "" });
  const load = async () => {
    try {
      const p = new URLSearchParams();
      if (filter.sku) p.set("sku", filter.sku);
      if (filter.warehouse_code) p.set("warehouse_code", filter.warehouse_code);
      const { data } = await api.get(`/inventory/balance?${p.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  return (
    <div className="space-y-6 page-fade" data-testid="inv-balance-page">
      <div><Crumb path={["Persediaan", "Saldo Persediaan"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Saldo Persediaan</h1></div>
      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>SKU</Label><Input value={filter.sku} onChange={(e) => setFilter({ ...filter, sku: e.target.value })} placeholder="SKU" data-testid="inv-filter-sku" /></div>
        <div><Label>Gudang</Label><Input value={filter.warehouse_code} onChange={(e) => setFilter({ ...filter, warehouse_code: e.target.value })} placeholder="Kode gudang" data-testid="inv-filter-wh" /></div>
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Nama</TableHead><TableHead>Gudang</TableHead><TableHead>Batch</TableHead><TableHead>Qty</TableHead><TableHead>Rata2 HPP</TableHead><TableHead>Nilai</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            {items.map((b, i) => (
              <TableRow key={i} data-testid={`inv-bal-row-${i}`}>
                <TableCell className="font-mono text-xs">{b.sku}</TableCell>
                <TableCell>{b.sku_name}</TableCell>
                <TableCell>{b.warehouse_name} <span className="text-xs text-slate-400">({b.warehouse_code})</span></TableCell>
                <TableCell className="font-mono text-xs">{b.batch_no || "-"}</TableCell>
                <TableCell className="font-mono-num">{b.qty}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(b.avg_cost)}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(b.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

export function StockCardPage() {
  const [sku, setSku] = useState("");
  const [wh, setWh] = useState("");
  const [data, setData] = useState(null);
  const load = async () => {
    if (!sku) return;
    try {
      const p = new URLSearchParams(); if (wh) p.set("warehouse_code", wh);
      const { data } = await api.get(`/inventory/stock-card/${sku}?${p.toString()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="inv-card-page">
      <div><Crumb path={["Persediaan", "Kartu Stok"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Kartu Stok</h1></div>
      <Card><CardContent className="p-4 flex gap-3 items-end flex-wrap">
        <div><Label>SKU</Label><Input value={sku} onChange={(e) => setSku(e.target.value)} data-testid="inv-card-sku" /></div>
        <div><Label>Gudang (opsional)</Label><Input value={wh} onChange={(e) => setWh(e.target.value)} data-testid="inv-card-wh" /></div>
        <Button onClick={load} data-testid="inv-card-load">Muat</Button>
      </CardContent></Card>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(data.balances || []).map((b, i) => (
              <Stat key={i} label={`${b.warehouse_code} ${b.batch_no ? "· " + b.batch_no : ""}`} value={`${b.qty} unit`} sub={`${formatRupiah(b.value)} · Rata2 ${formatRupiah(b.avg_cost)}`} tone="blue" testid={`inv-card-bal-${i}`} />
            ))}
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead>Gudang</TableHead><TableHead>Batch</TableHead><TableHead>Ref</TableHead><TableHead>Qty In</TableHead><TableHead>Qty Out</TableHead><TableHead>Unit Cost</TableHead><TableHead>Saldo Qty</TableHead><TableHead>Saldo Nilai</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.ledger || []).length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
                {(data.ledger || []).map((l, i) => (
                  <TableRow key={l.id} data-testid={`inv-card-ledger-${i}`}>
                    <TableCell className="text-xs">{formatDateID(l.date)}</TableCell>
                    <TableCell><Badge className="text-xs bg-slate-600">{l.movement_type}</Badge></TableCell>
                    <TableCell className="text-xs">{l.warehouse_code}</TableCell>
                    <TableCell className="text-xs">{l.batch_no || "-"}</TableCell>
                    <TableCell className="text-xs">{l.reference_number}</TableCell>
                    <TableCell className="font-mono-num">{l.qty_in || "-"}</TableCell>
                    <TableCell className="font-mono-num">{l.qty_out || "-"}</TableCell>
                    <TableCell className="font-mono-num">{formatRupiah(l.unit_cost)}</TableCell>
                    <TableCell className="font-mono-num">{l.balance_qty}</TableCell>
                    <TableCell className="font-mono-num">{formatRupiah(l.balance_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

/* ============ Mutasi (form + list) ============ */
export function MovementPage() {
  const can = useInvCan();
  const canWrite = can("INV_TRANSAKSI");
  const [form, setForm] = useState({ sku: "", warehouse_code: "", batch_no: "", movement_type: "PEMBELIAN", qty: 0, unit_cost: 0, date: new Date().toISOString().slice(0, 10) });
  const [list, setList] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/inventory/ledger?limit=100"); setList(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      await api.post("/inventory/movements", { ...form, qty: parseFloat(form.qty), unit_cost: parseFloat(form.unit_cost) });
      toast.success(`Mutasi ${form.movement_type} tercatat`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="inv-move-page">
      <div><Crumb path={["Persediaan", "Mutasi Persediaan"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Mutasi Persediaan</h1></div>
      <Card><CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Jenis Mutasi</Label>
            <Select value={form.movement_type} onValueChange={(v) => setForm({ ...form, movement_type: v })}>
              <SelectTrigger data-testid="inv-move-type"><SelectValue /></SelectTrigger>
              <SelectContent>{MOVEMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="inv-move-sku" /></div>
          <div><Label>Gudang</Label><Input value={form.warehouse_code} onChange={(e) => setForm({ ...form, warehouse_code: e.target.value })} data-testid="inv-move-wh" /></div>
          <div><Label>Batch (opsional)</Label><Input value={form.batch_no} onChange={(e) => setForm({ ...form, batch_no: e.target.value })} data-testid="inv-move-batch" /></div>
          <div><Label>Qty</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="inv-move-qty" /></div>
          <div><Label>Unit Cost</Label><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="inv-move-cost" /></div>
          <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="inv-move-date" /></div>
        </div>
        {canWrite ? (
          <Button onClick={submit} data-testid="inv-move-submit"><PlusCircle className="h-4 w-4 mr-2" />Simpan Mutasi</Button>
        ) : (
          <div className="text-xs text-slate-500" data-testid="inv-move-readonly">Mode baca — Anda tidak memiliki izin INV_TRANSAKSI.</div>
        )}
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead>SKU</TableHead><TableHead>Gudang</TableHead><TableHead>Batch</TableHead><TableHead>Qty In</TableHead><TableHead>Qty Out</TableHead><TableHead>Unit Cost</TableHead><TableHead>Saldo Qty</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            {list.slice(0, 50).map(l => (
              <TableRow key={l.id}>
                <TableCell className="text-xs">{formatDateID(l.date)}</TableCell>
                <TableCell><Badge className="text-xs bg-slate-600">{l.movement_type}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{l.sku}</TableCell>
                <TableCell>{l.warehouse_code}</TableCell>
                <TableCell className="text-xs">{l.batch_no || "-"}</TableCell>
                <TableCell className="font-mono-num">{l.qty_in || "-"}</TableCell>
                <TableCell className="font-mono-num">{l.qty_out || "-"}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(l.unit_cost)}</TableCell>
                <TableCell className="font-mono-num">{l.balance_qty}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

/* ============ Transfer ============ */
export function TransferPage() {
  const can = useInvCan();
  const canWrite = can("INV_TRANSFER");
  const [form, setForm] = useState({ sku: "", from_warehouse: "", to_warehouse: "", qty: 0, batch_no: "", date: new Date().toISOString().slice(0, 10) });
  const submit = async () => {
    try {
      await api.post("/inventory/transfer", { ...form, qty: parseFloat(form.qty) });
      toast.success("Transfer berhasil");
      setForm({ ...form, qty: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="inv-transfer-page">
      <div><Crumb path={["Persediaan", "Transfer Antar Gudang"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Transfer Antar Gudang</h1></div>
      <Card><CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="tr-sku" /></div>
          <div><Label>Dari Gudang</Label><Input value={form.from_warehouse} onChange={(e) => setForm({ ...form, from_warehouse: e.target.value })} data-testid="tr-from" /></div>
          <div><Label>Ke Gudang</Label><Input value={form.to_warehouse} onChange={(e) => setForm({ ...form, to_warehouse: e.target.value })} data-testid="tr-to" /></div>
          <div><Label>Batch (opsional)</Label><Input value={form.batch_no} onChange={(e) => setForm({ ...form, batch_no: e.target.value })} data-testid="tr-batch" /></div>
          <div><Label>Qty</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="tr-qty" /></div>
          <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tr-date" /></div>
        </div>
        {canWrite ? (
          <Button onClick={submit} data-testid="tr-submit"><ArrowRightLeft className="h-4 w-4 mr-2" />Pindahkan</Button>
        ) : (
          <div className="text-xs text-slate-500" data-testid="tr-readonly">Mode baca — Anda tidak memiliki izin INV_TRANSFER.</div>
        )}
        <div className="text-xs text-slate-500">Barang Dalam Perjalanan (COA 1302) akan digunakan sementara sebagai suspense — nilai saling menghapus setelah kedua sisi terbukukan.</div>
      </CardContent></Card>
    </div>
  );
}

/* ============ Adjustment ============ */
export function AdjustmentPage() {
  const can = useInvCan();
  const canWrite = can("INV_ADJUSTMENT");
  const [form, setForm] = useState({ sku: "", warehouse_code: "", batch_no: "", qty: 0, unit_cost: 0, adjustment_type: "PLUS", reason: "", date: new Date().toISOString().slice(0, 10) });
  const submit = async () => {
    try {
      await api.post("/inventory/adjustment", { ...form, qty: parseFloat(form.qty), unit_cost: parseFloat(form.unit_cost || 0) });
      toast.success(`Adjustment ${form.adjustment_type} tercatat`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="inv-adj-page">
      <div><Crumb path={["Persediaan", "Stock Adjustment"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Stock Adjustment</h1></div>
      <Card><CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Jenis</Label>
            <Select value={form.adjustment_type} onValueChange={(v) => setForm({ ...form, adjustment_type: v })}>
              <SelectTrigger data-testid="adj-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PLUS">Adjustment +</SelectItem>
                <SelectItem value="MINUS">Adjustment −</SelectItem>
                <SelectItem value="RUSAK">Barang Rusak</SelectItem>
                <SelectItem value="KEDALUWARSA">Kedaluwarsa</SelectItem>
                <SelectItem value="SAMPLE_PROMO">Sample/Promosi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="adj-sku" /></div>
          <div><Label>Gudang</Label><Input value={form.warehouse_code} onChange={(e) => setForm({ ...form, warehouse_code: e.target.value })} data-testid="adj-wh" /></div>
          <div><Label>Batch (opsional)</Label><Input value={form.batch_no} onChange={(e) => setForm({ ...form, batch_no: e.target.value })} data-testid="adj-batch" /></div>
          <div><Label>Qty</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="adj-qty" /></div>
          <div><Label>Unit Cost (PLUS baru)</Label><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="adj-cost" /></div>
          <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="adj-date" /></div>
          <div className="col-span-full"><Label>Alasan (wajib ≥5 karakter)</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="adj-reason" /></div>
        </div>
        {canWrite ? (
          <Button onClick={submit} data-testid="adj-submit"><PlusCircle className="h-4 w-4 mr-2" />Simpan Adjustment</Button>
        ) : (
          <div className="text-xs text-slate-500" data-testid="adj-readonly">Mode baca — Anda tidak memiliki izin INV_ADJUSTMENT.</div>
        )}
      </CardContent></Card>
    </div>
  );
}

/* ============ Opname (list + FSM actions) ============ */
export function OpnamePage() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/inventory/opname"); setList(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const action = async (act) => {
    if (!selected) return;
    try {
      await api.post(`/inventory/opname/${selected.id}/status`, { action: act });
      toast.success(`Opname → ${act}`);
      await load();
      setSelected(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="inv-opname-page">
      <div><Crumb path={["Persediaan", "Stock Opname"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Stock Opname</h1></div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Gudang</TableHead><TableHead>Baris</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Belum ada opname.</TableCell></TableRow>}
            {list.map(o => (
              <TableRow key={o.id} data-testid={`opn-row-${o.id}`}>
                <TableCell className="font-mono text-xs">{o.number}</TableCell>
                <TableCell>{formatDateID(o.date)}</TableCell>
                <TableCell>{o.warehouse_code}</TableCell>
                <TableCell>{(o.lines || []).length}</TableCell>
                <TableCell><Badge className="text-xs bg-slate-600">{o.status}</Badge></TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => setSelected(o)} data-testid={`opn-detail-${o.id}`}>Detail</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      {selected && (
        <Card><CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div><b>{selected.number}</b> · <Badge>{selected.status}</Badge></div>
            <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Tutup</Button>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Batch</TableHead><TableHead>Sistem</TableHead><TableHead>Fisik</TableHead><TableHead>Variance Qty</TableHead><TableHead>HPP</TableHead><TableHead>Variance Nilai</TableHead></TableRow></TableHeader>
            <TableBody>{(selected.lines || []).map((l, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{l.sku}</TableCell>
                <TableCell className="text-xs">{l.batch_no || "-"}</TableCell>
                <TableCell className="font-mono-num">{l.system_qty}</TableCell>
                <TableCell className="font-mono-num">{l.physical_qty}</TableCell>
                <TableCell className={`font-mono-num ${l.variance_qty < 0 ? "text-rose-700" : l.variance_qty > 0 ? "text-emerald-700" : ""}`}>{l.variance_qty}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(l.unit_cost)}</TableCell>
                <TableCell className={`font-mono-num ${l.variance_value < 0 ? "text-rose-700" : l.variance_value > 0 ? "text-emerald-700" : ""}`}>{formatRupiah(l.variance_value)}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
          <div className="flex gap-2 flex-wrap">
            {selected.status === "DRAFT" && <Button size="sm" onClick={() => action("DIHITUNG")} data-testid="opn-act-dihitung">Set DIHITUNG</Button>}
            {selected.status === "DIHITUNG" && <Button size="sm" onClick={() => action("DIREVIEW")} data-testid="opn-act-direview">Set DIREVIEW</Button>}
            {selected.status === "DIREVIEW" && <Button size="sm" onClick={() => action("DISETUJUI")} data-testid="opn-act-disetujui">Setujui</Button>}
            {selected.status === "DISETUJUI" && <Button size="sm" className="bg-emerald-600 text-white" onClick={() => action("DIPOSTING")} data-testid="opn-act-diposting">Posting Variance</Button>}
            {!["DIPOSTING", "DIBATALKAN"].includes(selected.status) && <Button size="sm" variant="destructive" onClick={() => action("DIBATALKAN")} data-testid="opn-act-batal">Batalkan</Button>}
          </div>
          <div className="text-xs text-slate-500">SoD: pembuat opname (non-Super Admin) tidak dapat menyetujui sendiri.</div>
        </CardContent></Card>
      )}
    </div>
  );
}

/* ============ Batch & Expiry ============ */
export function BatchPage() {
  const [items, setItems] = useState([]);
  const [nearDays, setNearDays] = useState(30);
  const load = async () => {
    try { const { data } = await api.get(`/inventory/batches?near_expiry_days=${nearDays}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [nearDays]);
  return (
    <div className="space-y-6 page-fade" data-testid="inv-batch-page">
      <div><Crumb path={["Persediaan", "Batch & Kedaluwarsa"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Batch & Kedaluwarsa</h1></div>
      <div className="flex gap-3 items-end">
        <div><Label>Near-expiry (hari)</Label><Input type="number" value={nearDays} onChange={(e) => setNearDays(parseInt(e.target.value) || 30)} className="w-24" data-testid="batch-near-days" /></div>
        <Button variant="outline" onClick={load} data-testid="batch-reload"><RefreshCw className="h-4 w-4 mr-2" />Muat Ulang</Button>
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Batch</TableHead><TableHead>Gudang</TableHead><TableHead>Produksi</TableHead><TableHead>Kedaluwarsa</TableHead><TableHead>Qty</TableHead><TableHead>Nilai</TableHead><TableHead>Sisa Hari</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            {items.map((b, i) => (
              <TableRow key={i} data-testid={`batch-row-${i}`}>
                <TableCell className="font-mono text-xs">{b.sku}</TableCell>
                <TableCell className="font-mono text-xs">{b.batch_no}</TableCell>
                <TableCell>{b.warehouse_code}</TableCell>
                <TableCell className="text-xs">{formatDateID(b.production_date)}</TableCell>
                <TableCell className="text-xs">{formatDateID(b.expiry_date)}</TableCell>
                <TableCell className="font-mono-num">{b.qty_on_hand}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(b.value_on_hand)}</TableCell>
                <TableCell className="font-mono-num">{b.days_to_expiry}</TableCell>
                <TableCell>
                  {b.expired ? <Badge className="bg-rose-600">KEDALUWARSA</Badge>
                   : b.near_expiry ? <Badge className="bg-amber-500">MENDEKATI</Badge>
                   : <Badge className="bg-emerald-600">AMAN</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

/* ============ Opening Balance ============ */
export function OpeningBalancePage() {
  const can = useInvCan();
  const canWrite = can("INV_OPENING_BALANCE");
  const [lines, setLines] = useState([{ sku: "", warehouse_code: "", batch_no: "", qty: 0, unit_cost: 0, production_date: "", expiry_date: "" }]);
  const [date, setDate] = useState("2026-01-01");
  const addLine = () => setLines([...lines, { sku: "", warehouse_code: "", batch_no: "", qty: 0, unit_cost: 0, production_date: "", expiry_date: "" }]);
  const setLine = (i, k, v) => { const c = [...lines]; c[i][k] = v; setLines(c); };
  const submit = async () => {
    try {
      const clean = lines.filter(l => l.sku && l.warehouse_code && parseFloat(l.qty) > 0);
      const { data } = await api.post("/inventory/opening", {
        date,
        lines: clean.map(l => ({ ...l, qty: parseFloat(l.qty), unit_cost: parseFloat(l.unit_cost) }))
      });
      toast.success(`${data.count} baris tersimpan (idempotent)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="inv-open-page">
      <div><Crumb path={["Persediaan", "Saldo Awal"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Saldo Awal Persediaan</h1></div>
      <Card><CardContent className="p-4 space-y-3">
        <div className="flex gap-3 items-end">
          <div><Label>Tanggal Migrasi</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="open-date" /></div>
          <Button variant="outline" onClick={addLine} data-testid="open-add-line">+ Tambah Baris</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Gudang</TableHead><TableHead>Batch</TableHead><TableHead>Qty</TableHead><TableHead>Unit Cost</TableHead><TableHead>Produksi</TableHead><TableHead>Kedaluwarsa</TableHead></TableRow></TableHeader>
          <TableBody>{lines.map((ln, i) => (
            <TableRow key={i}>
              <TableCell><Input value={ln.sku} onChange={(e) => setLine(i, "sku", e.target.value)} data-testid={`open-sku-${i}`} /></TableCell>
              <TableCell><Input value={ln.warehouse_code} onChange={(e) => setLine(i, "warehouse_code", e.target.value)} data-testid={`open-wh-${i}`} /></TableCell>
              <TableCell><Input value={ln.batch_no} onChange={(e) => setLine(i, "batch_no", e.target.value)} data-testid={`open-batch-${i}`} /></TableCell>
              <TableCell><Input type="number" value={ln.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`open-qty-${i}`} /></TableCell>
              <TableCell><Input type="number" value={ln.unit_cost} onChange={(e) => setLine(i, "unit_cost", e.target.value)} data-testid={`open-cost-${i}`} /></TableCell>
              <TableCell><Input type="date" value={ln.production_date} onChange={(e) => setLine(i, "production_date", e.target.value)} /></TableCell>
              <TableCell><Input type="date" value={ln.expiry_date} onChange={(e) => setLine(i, "expiry_date", e.target.value)} /></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
        {canWrite ? (
          <Button onClick={submit} data-testid="open-submit"><PlusCircle className="h-4 w-4 mr-2" />Posting Saldo Awal</Button>
        ) : (
          <div className="text-xs text-slate-500" data-testid="open-readonly">Mode baca — Anda tidak memiliki izin INV_OPENING_BALANCE.</div>
        )}
        <div className="text-xs text-slate-500">Posting idempotent — panggilan kedua dengan data yang sama tidak menambah duplikasi.</div>
      </CardContent></Card>
    </div>
  );
}

/* ============ Pengaturan HPP ============ */
export function CostingSettingsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [s, setS] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/inventory/settings/costing"); setS(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.put("/inventory/settings/costing", { method: s.method, allow_negative_stock: s.allow_negative_stock });
      toast.success("Pengaturan HPP tersimpan");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return <div className="p-6 text-slate-500">Memuat…</div>;
  return (
    <div className="space-y-6 page-fade" data-testid="inv-hpp-page">
      <div><Crumb path={["Persediaan", "Pengaturan HPP"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pengaturan HPP</h1></div>
      <Card><CardContent className="p-4 space-y-3">
        <div><Label>Metode Costing</Label>
          <Select value={s.method} onValueChange={(v) => setS({ ...s, method: v })}>
            <SelectTrigger className="w-64" data-testid="hpp-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="MOVING_AVERAGE">Moving Weighted Average (Default)</SelectItem></SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">FIFO/LIFO dapat ditambahkan di gelombang berikutnya tanpa merombak stock ledger.</p>
        </div>
        <div><Label>Izin Stok Negatif</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="checkbox" checked={!!s.allow_negative_stock} onChange={(e) => setS({ ...s, allow_negative_stock: e.target.checked })} data-testid="hpp-allow-neg" />
            <span className="text-sm text-slate-600">Izinkan mutasi keluar melebihi saldo (hanya Super Admin).</span>
          </div>
        </div>
        {isSuper ? (
          <Button onClick={save} data-testid="hpp-save"><Settings className="h-4 w-4 mr-2" />Simpan</Button>
        ) : (
          <div className="text-xs text-slate-500" data-testid="hpp-readonly">Mode baca — hanya Super Admin yang dapat mengubah pengaturan HPP.</div>
        )}
        <div className="text-xs text-slate-500">Terakhir diubah: {s.updated_at ? formatDateID(s.updated_at) : "-"} oleh {s.updated_by || "-"}</div>
      </CardContent></Card>
    </div>
  );
}

/* ============ Integrity Center ============ */
export function IntegrityPage() {
  const [d, setD] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/inventory/integrity"); setD(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const badgeCls = (s) => s === "SESUAI" ? "bg-emerald-600" : s === "PERLU_DITINJAU" ? "bg-amber-500" : s === "ERROR" ? "bg-rose-600" : "bg-slate-500";
  return (
    <div className="space-y-6 page-fade" data-testid="inv-integrity-page">
      <div>
        <Crumb path={["Persediaan", "Pusat Integritas Persediaan"]} />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pusat Integritas Persediaan</h1>
          {d && <Badge className={badgeCls(d.overall)} data-testid="inv-int-overall">{d.overall}</Badge>}
        </div>
      </div>
      <Card><CardContent className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Cek</TableHead><TableHead>Ekspektasi</TableHead><TableHead>Aktual</TableHead><TableHead>Selisih / Mismatch</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {(d?.checks || []).map((c, i) => (
              <TableRow key={i} data-testid={`inv-int-row-${i}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell className="font-mono-num">{c.expected !== undefined ? formatRupiah(c.expected) : "-"}</TableCell>
                <TableCell className="font-mono-num">{c.actual !== undefined ? formatRupiah(c.actual) : "-"}</TableCell>
                <TableCell className="font-mono-num">{c.mismatch_count !== undefined ? `${c.mismatch_count}×` : formatRupiah(c.difference || 0)}</TableCell>
                <TableCell><Badge className={badgeCls(c.status)}>{c.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      {d && <div className="text-xs text-slate-500">Digenerate {formatDateID(d.generated_at)}</div>}
    </div>
  );
}

